import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router";
import {
  Activity,
  ArrowRight,
  Handshake,
  LayoutGrid,
  MessageCircleQuestion,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import Images from "../../utils/Images";

const HERO_IMAGE_FALLBACK =
  "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&q=85";
const HERO_IMAGE = Images.hero_img || HERO_IMAGE_FALLBACK;
/** Secondary photo — experts / collaboration */
const HERO_SECONDARY_IMAGE =
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=85";

const QUESTIONS = [
  "How do I buy land safely in Ghana?",
  "How do I process a visa?",
  "Which health plan is best for me?",
  "How do I register a business in Ghana?",
  "How can I get a job abroad?",
  "How do I file for divorce in Ghana?",
];

const INTERVAL = 4200;

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const headlineLine = {
  hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.58, ease: [0.22, 1, 0.36, 1] },
  },
};

const headlineContainer = {
  hidden: {},
  visible: {
    transition: { delayChildren: 0.1, staggerChildren: 0.11 },
  },
};

const Hero = () => {
  const [qIndex, setQIndex] = useState(0);

  useEffect(() => {
    [HERO_IMAGE, HERO_SECONDARY_IMAGE].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setQIndex((i) => (i + 1) % QUESTIONS.length);
    }, INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative isolate min-h-[100svh] overflow-hidden bg-[#f7f4ec] pt-[6.5rem] sm:pt-[7rem]">
      <div className="absolute -left-24 top-0 -z-10 h-80 w-80 rounded-full bg-[#FFD221]/28 blur-3xl sm:-left-28 sm:top-2" />
      <div className="absolute left-1/2 top-2 -z-10 h-56 w-[min(24rem,92vw)] -translate-x-1/2 rounded-full bg-[#FFD221]/12 blur-[72px] sm:top-4 sm:h-64" />
      <div className="absolute left-[40%] top-1/2 -z-10 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FFD221]/8 blur-[90px]" />
      <div className="absolute bottom-8 -right-28 w-80 h-80 bg-white rounded-full blur-3xl -z-10" />

      <div className="relative z-10 mx-auto grid min-h-[calc(100svh-7rem)] w-full max-w-8xl items-start gap-10 px-4 pb-20 sm:gap-12 sm:px-6 lg:grid-cols-[1fr_1.12fr] lg:gap-x-8 lg:gap-y-10 lg:px-8 xl:gap-x-11">
        <div className="min-w-0 max-w-xl text-left lg:max-w-[42rem] xl:pr-0">
          <motion.h1
            className="max-w-2xl font-black tracking-[-0.035em] text-[#111]"
            variants={headlineContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.span
              variants={headlineLine}
              className="block whitespace-nowrap text-[clamp(1.65rem,4.5vw+0.75rem,2.15rem)] leading-[1.02] sm:text-[2.85rem] lg:text-[3.15rem]"
            >
              Start With Your Question
            </motion.span>
            <motion.span
              variants={headlineLine}
              className="block mt-2 max-w-full w-fit"
            >
              <span className="relative flex w-fit max-w-full items-stretch overflow-hidden rounded-2xl border border-black/10 bg-white shadow-[0_14px_45px_-28px_rgba(0,0,0,0.38)]">
                <span
                  className="w-[5px] shrink-0 bg-[#FFD221] sm:w-1.5"
                  aria-hidden
                />
                <span className="relative flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-3 pr-3 sm:gap-3 sm:py-3 sm:pl-4 sm:pr-5">
                  <span
                    className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 rounded-tl-md border-l-2 border-t-2 border-black/12 sm:left-3.5 sm:top-2.5"
                    aria-hidden
                  />
                  <span
                    className="pointer-events-none absolute bottom-2 right-2.5 h-4 w-4 rounded-br-md border-b-2 border-r-2 border-[#FFD221] sm:bottom-2.5 sm:right-3.5"
                    aria-hidden
                  />
                  <span
                    className="hidden h-px w-5 shrink-0 bg-gradient-to-r from-[#FFD221] to-transparent sm:block sm:w-8"
                    aria-hidden
                  />
                  <span className="min-w-0 text-[1.65rem] font-black leading-[1.05] tracking-[-0.035em] text-neutral-900 sm:text-[2.25rem] lg:text-[2.5rem]">
                    End With the
                  </span>
                  <span
                    className="hidden h-px w-5 shrink-0 bg-gradient-to-l from-[#FFD221] to-transparent sm:block sm:w-8"
                    aria-hidden
                  />
                </span>
              </span>
            </motion.span>
            <motion.span
              variants={headlineLine}
              className="relative mt-1 block w-fit text-[clamp(1.65rem,4.5vw+0.75rem,2.15rem)] leading-[1.02] sm:text-[2.85rem] lg:text-[3.15rem]"
            >
              Right Decision.
              <motion.span
                className="absolute -bottom-1 left-0 h-[6px] w-full rounded-full bg-[#FFD221] sm:-bottom-1.5 sm:h-[7px]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                style={{ originX: 0 }}
                transition={{
                  delay: 0.72,
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                }}
                aria-hidden
              />
            </motion.span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            className="mt-6 max-w-lg text-base leading-relaxed text-gray-700 sm:text-lg"
            {...fadeUp(0.13)}
          >
            InfoBoxx helps you understand your options first — then connects you
            to trusted experts when you&apos;re ready to act.
          </motion.p>

          {/* Animated question card */}
          <motion.div
            className="group/card relative mt-7 w-full max-w-xl overflow-hidden rounded-2xl border border-black/10 bg-white shadow-[0_24px_70px_-32px_rgba(0,0,0,0.48)] ring-1 ring-inset ring-white/60"
            {...fadeUp(0.2)}
          >
            {/* Top accent */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-transparent via-[#FFD221] to-transparent opacity-90"
              aria-hidden
            />

            {/* Card header strip */}
            <div className="relative border-b border-black/[0.06] bg-gradient-to-br from-[#f7f4ec] via-white to-[#fffef5] px-4 py-3.5 sm:px-5 sm:py-4">
              <div
                className="pointer-events-none absolute -right-16 -top-12 h-32 w-32 rounded-full bg-[#FFD221]/15 blur-2xl"
                aria-hidden
              />
              <div className="flex relative flex-wrap gap-y-2 gap-x-3 items-center">
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-2.5">
                  <span className="flex justify-center items-center w-8 h-8 bg-white rounded-xl border shadow-sm shrink-0 border-black/8">
                    <Activity
                      className="h-4 w-4 text-[#b89800]"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-black uppercase leading-tight tracking-[0.12em] text-black/70 sm:text-xs sm:tracking-[0.14em]">
                      People are asking right now
                    </p>
                    <p className="mt-0.5 text-[10px] font-semibold text-black/40 sm:text-[11px]">
                      Real questions Ghanaians search every day
                    </p>
                  </div>
                </div>
                <span className="ml-auto inline-flex shrink-0 tabular-nums rounded-lg border border-black/8 bg-white/90 px-2 py-1 text-[10px] font-black text-black/45 shadow-sm sm:px-2.5">
                  {qIndex + 1} / {QUESTIONS.length}
                </span>
              </div>
            </div>

            {/* Question body */}
            <div className="relative px-4 pt-3 pb-2 sm:px-5 sm:pb-2 sm:pt-4">
              <div className="relative overflow-hidden rounded-xl border border-black/[0.06] bg-[#f7f4ec]/90 px-4 py-3.5 shadow-inner sm:px-5 sm:py-4">
                <div
                  className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-[#FFD221]/20 blur-2xl"
                  aria-hidden
                />
                <span
                  className="pointer-events-none absolute left-2 top-0 select-none font-serif text-[3.75rem] font-black leading-none text-[#FFD221]/35 sm:left-3 sm:text-[4.5rem]"
                  aria-hidden
                >
                  &ldquo;
                </span>

                <div className="relative z-[1] min-h-[3.25rem] pl-1 sm:min-h-[3.6rem]">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.p
                      key={qIndex}
                      initial={{
                        opacity: 0,
                        y: 20,
                        filter: "blur(8px)",
                        scale: 0.98,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        filter: "blur(0px)",
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        y: -18,
                        filter: "blur(8px)",
                        scale: 0.98,
                      }}
                      transition={{
                        duration: 0.5,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="relative z-10 text-[1.08rem] font-black leading-snug tracking-tight text-black sm:text-xl sm:leading-snug"
                    >
                      {QUESTIONS[qIndex]}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>

              {/* Dot indicators */}
              <div className="mt-3 flex flex-wrap items-center gap-2 sm:mt-3.5">
                <div className="flex flex-1 items-center gap-1.5 sm:gap-2">
                  {QUESTIONS.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Show question ${i + 1}`}
                      aria-current={i === qIndex ? "true" : undefined}
                      onClick={() => setQIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD221] focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                        i === qIndex
                          ? "w-8 bg-[#FFD221] shadow-sm"
                          : "w-2 bg-black/15 hover:bg-black/25"
                      }`}
                    />
                  ))}
                </div>
                <span className="hidden text-[10px] font-semibold uppercase tracking-wider text-black/35 sm:inline">
                  Next in a few seconds
                </span>
              </div>
            </div>

            {/* Progress */}
            <div className="px-4 pb-4 sm:px-5">
              <div className="flex gap-3 items-center">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.08]">
                  <motion.div
                    key={qIndex}
                    className="h-full origin-left rounded-full bg-gradient-to-r from-[#e6c200] to-[#FFD221]"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{
                      duration: INTERVAL / 1000,
                      ease: "linear",
                    }}
                  />
                </div>
                <span className="w-9 shrink-0 text-right text-[10px] font-bold tabular-nums text-black/35">
                  {(INTERVAL / 1000).toFixed(0)}s
                </span>
              </div>
            </div>
          </motion.div>

          {/* Plain language line */}
          <motion.p
            className="mt-5 max-w-lg text-[13px] leading-relaxed text-gray-600 sm:text-sm"
            {...fadeUp(0.26)}
          >
            Most people don&apos;t start with ads — they start with questions.
            InfoBoxx is built for that exact moment.{" "}
            <span className="font-semibold text-gray-900">
              Clear info first. Verified experts when you&apos;re ready.
            </span>
          </motion.p>

          {/* CTAs — grid keeps partners row aligned when the 3rd wraps */}
          <motion.div
            className="grid grid-cols-1 gap-3 mt-8 sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-4 sm:gap-y-3"
            {...fadeUp(0.3)}
          >
            <motion.button
              type="button"
              onClick={() => scrollTo("categories")}
              className="inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#FFD221] px-6 py-3.5 text-sm font-black text-black shadow-[0_12px_40px_-18px_rgba(0,0,0,0.6)] transition hover:bg-[#ffdf44]"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <LayoutGrid size={17} strokeWidth={2.5} aria-hidden />
              Explore Categories
              <ArrowRight size={17} strokeWidth={2.5} />
            </motion.button>

            <Link
              to="/contact"
              className="group inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-black/15 bg-white px-6 py-3.5 text-sm font-semibold text-black shadow-sm transition hover:border-black"
            >
              <MessageCircleQuestion size={18} strokeWidth={2} aria-hidden />
              Ask a Question
              <ArrowRight
                size={16}
                strokeWidth={2.5}
                className="transition group-hover:translate-x-0.5"
              />
            </Link>

            <Link
              to="/partners#how-to-become-a-partner"
              className="group inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#FFD221] bg-gradient-to-b from-[#FFD221]/40 via-[#FFD221]/18 to-white px-6 py-3.5 text-sm font-black text-black shadow-[0_12px_40px_-20px_rgba(229,184,0,0.65)] ring-1 ring-black/[0.06] transition hover:from-[#FFD221]/55 hover:via-[#FFD221]/28 hover:shadow-[0_16px_44px_-18px_rgba(0,0,0,0.35)] sm:col-span-2 sm:max-w-md sm:justify-self-center lg:col-span-1 lg:max-w-none"
            >
              <Handshake size={18} strokeWidth={2.5} className="shrink-0" aria-hidden />
              Become a Partner
              <ArrowRight
                size={17}
                strokeWidth={2.5}
                className="transition group-hover:translate-x-0.5"
                aria-hidden
              />
            </Link>
          </motion.div>
        </div>

        {/* Right column: bento-style visual — self-contained, won’t crowd copy */}
        <motion.div
          className="relative mx-auto mb-10 w-full max-w-[26.5rem] shrink-0 sm:mb-12 sm:max-w-[30rem] lg:mx-0 lg:mb-0 lg:ml-auto lg:max-w-none lg:w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="pointer-events-none absolute -right-6 top-[15%] -z-10 h-56 w-56 rounded-full bg-[#FFD221]/22 blur-[56px] lg:-right-4"
            aria-hidden
          />

          <div className="relative overflow-hidden rounded-[1.65rem] border border-black/10 bg-white shadow-[0_28px_70px_-32px_rgba(0,0,0,0.4)] sm:rounded-[1.85rem]">
            <div
              className="absolute bottom-6 left-0 top-8 w-1 rounded-full bg-[#FFD221] sm:bottom-8 sm:top-10"
              aria-hidden
            />

            <div className="relative pt-3 pr-3 pb-4 pl-4 sm:pl-5 sm:pr-4 sm:pt-4 sm:pb-5 lg:pl-6 lg:pr-5 lg:pb-6">
              {/* Main feature image */}
              <div className="relative mb-3 overflow-hidden rounded-2xl border border-black/[0.06] sm:mb-4 lg:rounded-[1.35rem]">
                <div className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[16/10.5]">
                  <img
                    src={HERO_IMAGE}
                    alt="People finding clear answers on InfoBoxx"
                    className="object-cover w-full h-full"
                    fetchPriority="high"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t pointer-events-none from-black/60 via-black/20 to-black/10" />
                <motion.div
                  className="absolute right-2.5 top-2.5 sm:right-3 sm:top-3"
                  initial={{ opacity: 0, scale: 0.85, x: 12 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ delay: 0.45, type: "spring", stiffness: 280, damping: 22 }}
                >
                  <motion.span
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FFD221] px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wide text-black shadow-md sm:px-3 sm:text-[11px]"
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <TrendingUp className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" strokeWidth={2.5} aria-hidden />
                    Real searches
                  </motion.span>
                </motion.div>
                <motion.div
                  className="absolute bottom-2.5 left-2.5 right-2.5 rounded-xl border border-white/25 bg-black/50 px-3 py-2.5 shadow-lg backdrop-blur-md sm:bottom-3 sm:left-3 sm:right-3 sm:px-4 sm:py-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.45 }}
                >
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-white sm:text-[11px] sm:tracking-[0.14em]">
                    Question → clarity → action
                  </p>
                  <p className="mt-1 text-xs font-bold leading-snug text-white sm:text-sm">
                    Start with what you need to know — then move when you&apos;re ready.
                  </p>
                </motion.div>
              </div>

              {/* Bento row: secondary shot + stat */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-5 sm:gap-4">
                <motion.div
                  className="relative aspect-[5/4] overflow-hidden rounded-xl border border-black/[0.07] bg-[#f7f4ec] shadow-sm sm:col-span-2 sm:aspect-square"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35, duration: 0.5 }}
                >
                  <img
                    src={HERO_SECONDARY_IMAGE}
                    alt="Connecting with verified experts on InfoBoxx"
                    className="object-cover w-full h-full"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/[0.05]" />
                  <motion.div
                    className="absolute left-2 bottom-2 rounded-lg bg-black/65 px-2 py-1 text-[8px] font-black uppercase tracking-wider text-white backdrop-blur-sm sm:left-2.5 sm:bottom-2.5 sm:text-[9px]"
                    animate={{ opacity: [0.85, 1, 0.85] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    Experts
                  </motion.div>
                </motion.div>

                <motion.div
                  className="flex flex-col gap-3 rounded-xl border border-black/[0.08] bg-gradient-to-br from-[#f7f4ec] to-white px-3.5 py-3.5 shadow-sm sm:col-span-3 sm:gap-3.5 sm:px-4 sm:py-4"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <div className="flex gap-2 items-center">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFD221] shadow-sm sm:h-9 sm:w-9">
                      <ShieldCheck className="h-4 w-4 text-black sm:h-[18px] sm:w-[18px]" strokeWidth={2.25} aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black uppercase tracking-widest text-black/45 sm:text-[10px]">
                        This week
                      </p>
                      <p className="text-lg font-black tabular-nums leading-none text-black sm:text-xl">
                        2,418+
                      </p>
                      <p className="mt-0.5 text-[9px] font-semibold text-black/40 sm:text-[10px]">
                        High-intent lookups on InfoBoxx
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[11px] font-bold leading-snug text-black sm:text-xs">
                      Searches for property &amp; legal answers from people like you.
                    </p>
                    
                  </div>

                

                  <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
                    <div className="rounded-lg bg-white/80 px-2.5 py-2 ring-1 ring-black/[0.07] sm:px-3 sm:py-2.5">
                      <p className="text-[8px] font-black uppercase tracking-wide text-black/40 sm:text-[9px]">
                        Topic areas
                      </p>
                      <p className="mt-0.5 text-sm font-black text-black">6 industries</p>
                    </div>
                    <div className="rounded-lg bg-white/80 px-2.5 py-2 ring-1 ring-black/[0.07] sm:px-3 sm:py-2.5">
                      <p className="text-[8px] font-black uppercase tracking-wide text-black/40 sm:text-[9px]">
                        Expert network
                      </p>
                      <p className="mt-0.5 text-sm font-black text-black">50+ partners</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    <span className="rounded-full border border-black/10 bg-white px-2 py-0.5 text-[8px] font-black uppercase tracking-wide text-black/70 sm:text-[9px]">
                      Info first
                    </span>
                    <span className="rounded-full border border-black/10 bg-[#FFD221]/35 px-2 py-0.5 text-[8px] font-black uppercase tracking-wide text-black sm:text-[9px]">
                      Verified next
                    </span>
                    <span className="rounded-full border border-black/10 bg-white px-2 py-0.5 text-[8px] font-black uppercase tracking-wide text-black/70 sm:text-[9px]">
                      Ghana-wide
                    </span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Connector hint — subtle motion, stays inside right column */}
          <motion.div
            className="flex absolute -bottom-6 left-1/2 gap-1 items-center -translate-x-1/2 pointer-events-none sm:-bottom-7"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 1 }}
            aria-hidden
          >
            <motion.span
              className="w-10 h-px bg-gradient-to-r from-transparent to-black/20 sm:w-14"
              animate={{ scaleX: [0.85, 1, 0.85], opacity: [0.4, 0.8, 0.4] }}
              transition={{ duration: 2.8, repeat: Infinity }}
            />
            <span className="rounded-full bg-[#FFD221]/90 px-2 py-0.5 text-[8px] font-black uppercase tracking-widest text-black">
              Flow
            </span>
            <motion.span
              className="w-10 h-px bg-gradient-to-l from-transparent to-black/20 sm:w-14"
              animate={{ scaleX: [0.85, 1, 0.85], opacity: [0.4, 0.8, 0.4] }}
              transition={{ duration: 2.8, repeat: Infinity, delay: 0.2 }}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
